/**
 * useStorefrontAuth Hook
 * Handles authentication logic for store-front
 */

import { useCallback } from 'react';

import { Location, useNavigate } from 'react-router-dom';

import {
    authenticate,
    logout,
    selectAuthError,
    selectIsAuthenticated,
    selectIsAuthLoading,
    signIn,
} from '../store/slices';
import { getSessionCookie, removeSessionCookie, setSessionCookie } from '../utils/cookies';
import { routes } from '../utils/routes';
import { isValidAccessToken, sanitizeAccessToken, sanitizePin } from '../utils/validation';
import { useAppDispatch } from './use-app-dispatch';
import { useAppSelector } from './use-app-selector';

interface ParsedCredentials {
    accessToken: string;
    pin: string;
}

export interface UseStorefrontAuthReturn {
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    parseCredentialsFromUrl: (location: Location, searchParams: URLSearchParams) => ParsedCredentials | null;
    checkExistingSession: () => Promise<boolean>;
    performLogin: (accessToken: string, pin: string) => Promise<boolean>;
    performLogout: () => void;
    isValidAccessToken: (token: string) => boolean;
}

export const useStorefrontAuth = (): UseStorefrontAuthReturn => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const isLoading = useAppSelector(selectIsAuthLoading);
    const error = useAppSelector(selectAuthError);

    /**
     * Parse credentials from URL hash or query params
     * Supports formats:
     *   - Hash: #accessToken:pin
     *   - Query: ?accessToken:pin
     */
    const parseCredentialsFromUrl = useCallback(
        (location: Location, _searchParams: URLSearchParams): ParsedCredentials | null => {
            // Handle hash-based credentials (#accessToken:pin)
            if (location.hash && location.hash.includes(':')) {
                const hashContent = location.hash.slice(1); // Remove #
                const [accessToken, pin] = hashContent.split(':');

                if (accessToken && pin) {
                    const sanitizedToken = sanitizeAccessToken(accessToken);
                    const sanitizedPin = sanitizePin(pin);

                    if (sanitizedToken && sanitizedPin) {
                        return {
                            accessToken: sanitizedToken,
                            pin: sanitizedPin,
                        };
                    }
                }
            }

            // Handle query param credentials (?accessToken:pin)
            const queryString = location.search.slice(1); // Remove ?
            if (queryString.includes(':')) {
                const [accessToken, pin] = queryString.split(':');

                if (accessToken && pin) {
                    const sanitizedToken = sanitizeAccessToken(accessToken);
                    const sanitizedPin = sanitizePin(pin);

                    if (sanitizedToken && sanitizedPin) {
                        return {
                            accessToken: sanitizedToken,
                            pin: sanitizedPin,
                        };
                    }
                }
            }

            return null;
        },
        [],
    );

    /**
     * Check for existing session in cookies and authenticate
     */
    const checkExistingSession = useCallback(async (): Promise<boolean> => {
        const session = getSessionCookie();

        if (session?.session_id && session?.session_token) {
            try {
                await dispatch(authenticate(session)).unwrap();
                return true;
            } catch {
                removeSessionCookie();
                return false;
            }
        }

        return false;
    }, [dispatch]);

    /**
     * Perform login with credentials
     */
    const performLogin = useCallback(
        async (accessToken: string, pin: string): Promise<boolean> => {
            try {
                const result = await dispatch(signIn({ accessToken, pin })).unwrap();

                // Save session to cookie
                setSessionCookie({
                    session_id: result.session_id,
                    session_token: result.session_token,
                });

                return true;
            } catch {
                return false;
            }
        },
        [dispatch],
    );

    /**
     * Perform logout and clear session
     */
    const performLogout = useCallback(() => {
        removeSessionCookie();
        dispatch(logout());
        navigate(routes.WELCOME);
    }, [dispatch, navigate]);

    return {
        isAuthenticated,
        isLoading,
        error,
        parseCredentialsFromUrl,
        checkExistingSession,
        performLogin,
        performLogout,
        isValidAccessToken,
    };
};

export default useStorefrontAuth;
